import { useEffect, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { api } from "../lib/api";
import Seo from "../components/Seo";
import SiteFooter from "../components/SiteFooter";
import { toast } from "sonner";
import {
  Calendar, Clock, ArrowLeft, ArrowRight, CheckCircle2, Loader2,
} from "lucide-react";

/* /m/:handle/book — public booking page for a member.
   Visitors pick a date, see open slots, fill name/email, hit submit. */

const WINE = "#0F172A";
const O    = "#F39A1F";
const G    = "#2E9F3A";
const NAVY = "#0F172A";
const GOLD = "#E0F2FE";
const CREAM = "#FFFFFF";

const DAY_NAMES = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function toIsoDate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function prettyDate(iso) {
  const [y, m, d] = iso.split("-").map(Number);
  const dt = new Date(y, m - 1, d);
  return `${DAY_NAMES[dt.getDay()]}, ${MONTHS[dt.getMonth()]} ${dt.getDate()}`;
}

function prettyTime(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m).padStart(2, "0")} ${ampm}`;
}

export default function BookingPublicPage() {
  const { handle } = useParams();
  const [info, setInfo] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [loading, setLoading] = useState(true);

  const [selectedDate, setSelectedDate] = useState(null);
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedTime, setSelectedTime] = useState(null);

  const [form, setForm] = useState({ name: "", email: "", phone: "", note: "" });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    api.get(`/book/${handle}/info`)
      .then((r) => setInfo(r.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [handle]);

  useEffect(() => {
    if (!selectedDate) return;
    setSlotsLoading(true);
    setSelectedTime(null);
    api.get(`/book/${handle}/slots`, { params: { date: selectedDate } })
      .then((r) => setSlots(r.data?.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setSlotsLoading(false));
  }, [selectedDate, handle]);

  if (notFound) return <Navigate to="/" replace />;
  if (loading || !info) {
    return <div className="min-h-screen flex items-center justify-center" style={{ background: CREAM }}>
      <p className="italic text-sm" style={{ color: WINE }}>Loading…</p>
    </div>;
  }

  const days = [];
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  for (let i = 0; i <= info.days_advance; i++) {
    const d = new Date(today);
    d.setDate(d.getDate() + i);
    days.push(d);
  }

  const submit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) return toast.error("Please pick a date and time first.");
    if (!form.name.trim() || !form.email.trim()) return toast.error("Please add your name and email.");
    setSubmitting(true);
    try {
      await api.post(`/book/${handle}/book`, {
        date: selectedDate,
        time: selectedTime,
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        note: form.note.trim(),
      });
      setDone(true);
      toast.success("★ Booked! Check your email.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't book. Please try again.");
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: CREAM }} data-testid="booking-public-page">
      <Seo
        title={`${info.page_title} — ${info.full_name}`}
        description={info.intro}
        canonical={`https://faitharama.com/m/${info.handle}/book`}
        image={info.photo_url}
      />

      {/* Slim header */}
      <header className="border-b-2" style={{ borderColor: `${O}55`, background: "linear-gradient(180deg, #FFFFFF 0%, #E0F2FE 100%)" }}>
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-3 flex items-center justify-between gap-3">
          <Link to={`/m/${info.handle}`}
                data-testid="booking-back-mirror"
                className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] font-bold"
                style={{ color: WINE }}>
            <ArrowLeft className="w-3.5 h-3.5"/> Back to {info.full_name?.split(" ")[0] || "page"}
          </Link>
          <Link to="/" className="font-serif-display text-xl uppercase" style={{ color: WINE }}>
            Faitharama<span style={{ color: O }}>.com</span>
          </Link>
        </div>
      </header>

      {/* Hero / intro */}
      <section className="border-b-2"
               style={{ background: `linear-gradient(180deg, ${NAVY} 0%, #1F2E5C 100%)`, borderColor: O }}>
        <div className="max-w-4xl mx-auto px-5 md:px-8 py-10 md:py-14 text-center">
          {info.photo_url && (
            <div className="mx-auto rounded-full overflow-hidden shadow-2xl mb-4" style={{ width: 120, height: 120, border: `4px solid ${O}` }}>
              <img src={info.photo_url} alt={info.full_name} className="w-full h-full object-cover"/>
            </div>
          )}
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[10px] tracking-[0.32em] uppercase font-bold mb-3" style={{ background: O, color: "#0F172A" }}>
            <Calendar className="w-3.5 h-3.5"/> ★ Pick A Time
          </div>
          <h1 className="font-serif-display text-3xl md:text-5xl uppercase leading-[1.05]" style={{ color: GOLD, textWrap: "balance" }}>
            {info.page_title}
          </h1>
          <p className="mt-3 text-base md:text-lg text-white/85 max-w-2xl mx-auto">
            {info.intro}
          </p>
          <div className="mt-4 inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase font-bold" style={{ color: "rgba(255,229,166,0.85)" }}>
            <Clock className="w-3.5 h-3.5"/> {info.duration_min}-Minute Appointment
          </div>
        </div>
      </section>

      <div className="max-w-4xl mx-auto px-5 md:px-8 py-8 space-y-5">

        {done ? (
          <div className="rounded-2xl bg-white border-2 shadow-md p-6 md:p-8 text-center" style={{ borderColor: G }} data-testid="booking-success">
            <div className="mx-auto w-14 h-14 rounded-full flex items-center justify-center mb-3" style={{ background: G }}>
              <CheckCircle2 className="w-8 h-8 text-white"/>
            </div>
            <h2 className="font-serif-display text-3xl uppercase" style={{ color: WINE }}>
              You're On The Calendar!
            </h2>
            <p className="mt-3 text-base" style={{ color: NAVY }}>
              {prettyDate(selectedDate)} at <strong>{prettyTime(selectedTime)}</strong>
            </p>
            <p className="mt-2 text-sm italic text-foreground/70">
              We just sent you a confirmation email. {info.full_name?.split(" ")[0]} will be in touch.
            </p>
          </div>
        ) : (
          <>
            {/* STEP 1 — pick a date */}
            <div className="rounded-2xl bg-white border-2 shadow-md p-5" style={{ borderColor: O }}>
              <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-2" style={{ color: O }}>
                ★ STEP 1 — PICK A DAY
              </div>
              <div className="grid grid-cols-3 sm:grid-cols-5 md:grid-cols-7 gap-2" data-testid="booking-date-grid">
                {days.map((d) => {
                  const iso = toIsoDate(d);
                  const isSelected = selectedDate === iso;
                  return (
                    <button key={iso}
                            type="button"
                            onClick={() => setSelectedDate(iso)}
                            data-testid={`booking-date-${iso}`}
                            className="rounded-lg p-3 text-center hover:shadow transition border-2"
                            style={{
                              background: isSelected ? WINE : "#FFFFFF",
                              borderColor: isSelected ? WINE : `${O}55`,
                              color: isSelected ? GOLD : WINE,
                            }}>
                      <div className="text-[10px] uppercase tracking-[0.18em] font-bold">{DAY_NAMES[d.getDay()]}</div>
                      <div className="font-serif-display text-2xl leading-none mt-0.5">{d.getDate()}</div>
                      <div className="text-[10px] uppercase mt-0.5">{MONTHS[d.getMonth()]}</div>
                    </button>
                  );
                })}
              </div>
            </div>

            {/* STEP 2 — pick a time */}
            {selectedDate && (
              <div className="rounded-2xl bg-white border-2 shadow-md p-5" style={{ borderColor: NAVY }}>
                <div className="text-[10px] tracking-[0.32em] uppercase font-bold mb-2" style={{ color: NAVY }}>
                  ★ STEP 2 — PICK A TIME ({prettyDate(selectedDate)})
                </div>
                {slotsLoading ? (
                  <div className="py-6 text-center italic text-foreground/60 text-sm inline-flex items-center gap-2 w-full justify-center">
                    <Loader2 className="w-4 h-4 animate-spin"/> Looking up open times…
                  </div>
                ) : slots.length === 0 ? (
                  <p className="text-sm italic text-foreground/60 py-3">
                    No open times that day. Try another date above.
                  </p>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="booking-slot-grid">
                    {slots.map((t) => (
                      <button key={t} type="button"
                              onClick={() => setSelectedTime(t)}
                              data-testid={`booking-slot-${t}`}
                              className="rounded-md py-2.5 px-2 text-sm font-bold border-2 hover:shadow transition"
                              style={{
                                background: selectedTime === t ? G : "#FFFFFF",
                                borderColor: selectedTime === t ? G : `${NAVY}55`,
                                color: selectedTime === t ? "#FFFFFF" : NAVY,
                              }}>
                        {prettyTime(t)}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* STEP 3 — your details */}
            {selectedDate && selectedTime && (
              <form onSubmit={submit}
                    data-testid="booking-form"
                    className="rounded-2xl bg-white border-2 shadow-md p-5 space-y-3" style={{ borderColor: G }}>
                <div className="text-[10px] tracking-[0.32em] uppercase font-bold" style={{ color: G }}>
                  ★ STEP 3 — YOUR DETAILS
                </div>
                <p className="text-sm" style={{ color: WINE }}>
                  Booking <strong>{prettyDate(selectedDate)}</strong> at <strong>{prettyTime(selectedTime)}</strong>
                </p>
                <div className="grid sm:grid-cols-2 gap-3">
                  <input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                         data-testid="booking-name"
                         placeholder="Your first name"
                         className="px-3 py-3 rounded-md text-base outline-none focus:ring-2"
                         style={{ background: "#FFFFFF", border: `2px solid ${O}55`, color: WINE }}/>
                  <input value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
                         type="email"
                         data-testid="booking-email"
                         placeholder="Your email"
                         className="px-3 py-3 rounded-md text-base outline-none focus:ring-2"
                         style={{ background: "#FFFFFF", border: `2px solid ${O}55`, color: WINE }}/>
                </div>
                <input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                       data-testid="booking-phone"
                       placeholder="Phone (optional)"
                       className="w-full px-3 py-3 rounded-md text-base outline-none focus:ring-2"
                       style={{ background: "#FFFFFF", border: `2px solid ${O}55`, color: WINE }}/>
                <textarea value={form.note} onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
                          data-testid="booking-note"
                          rows={3} maxLength={1000}
                          placeholder="Anything I should know before we talk? (optional)"
                          className="w-full px-3 py-3 rounded-md text-base outline-none focus:ring-2"
                          style={{ background: "#FFFFFF", border: `2px solid ${O}55`, color: WINE }}/>
                <button type="submit" disabled={submitting}
                        data-testid="booking-submit"
                        className="w-full inline-flex items-center justify-center gap-2 px-7 py-4 rounded-md text-base font-bold uppercase tracking-[0.18em] text-white shadow-lg hover:scale-[1.02] transition disabled:opacity-50"
                        style={{ background: G }}>
                  {submitting ? "Booking…" : <>Book This Time <ArrowRight className="w-4 h-4"/></>}
                </button>
                <p className="text-[11px] italic text-center text-foreground/55">
                  We'll email a confirmation right after you book.
                </p>
              </form>
            )}
          </>
        )}
      </div>

      <SiteFooter />
    </div>
  );
}
